/**
 * Axis + scale helpers (Phase 3, §6.4) — SPINE. Recessive hairline axes: light
 * gridlines, an emphasised zero baseline, de-DE tick labels (the caller formats
 * values through `lib/format.ts`). The scale/tick maths here is shared by every
 * chart primitive so their geometry stays consistent.
 */

/** Classic "nice number" rounding for axis steps. */
export function niceNum(range: number, round: boolean): number {
  const safeRange = range || 1;
  const exponent = Math.floor(Math.log10(safeRange));
  const fraction = safeRange / Math.pow(10, exponent);
  let niceFraction: number;
  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else if (fraction <= 1) niceFraction = 1;
  else if (fraction <= 2) niceFraction = 2;
  else if (fraction <= 5) niceFraction = 5;
  else niceFraction = 10;
  return niceFraction * Math.pow(10, exponent);
}

export interface NiceScale {
  min: number;
  max: number;
  ticks: number[];
}

/** A rounded domain + evenly spaced tick values covering `[dataMin, dataMax]`. */
export function niceTicks(dataMin: number, dataMax: number, maxTicks = 5): NiceScale {
  let min = dataMin;
  let max = dataMax;
  if (min === max) {
    min -= 1;
    max += 1;
  }
  const range = niceNum(max - min, false);
  // Clamp the step to a whole euro: tick labels render with 0 fraction digits
  // (`moneyShort`), so sub-unit steps would produce duplicate rounded labels
  // (e.g. an all-zero series ticking -1/-0.5/0/0.5/1 → „-1 € -1 € 0 € 1 € 1 €“).
  const step = Math.max(1, niceNum(range / Math.max(1, maxTicks - 1), true) || 1);
  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step * 0.5; v += step) {
    ticks.push(Math.round(v * 100) / 100);
  }
  return { min: niceMin, max: niceMax, ticks };
}

/** A linear map from a data domain to a pixel range. */
export function scaleLinear(
  domainMin: number,
  domainMax: number,
  rangeMin: number,
  rangeMax: number,
): (value: number) => number {
  const span = domainMax - domainMin || 1;
  return (value: number) => rangeMin + ((value - domainMin) / span) * (rangeMax - rangeMin);
}

export interface AxisTick {
  /** Pixel coordinate (y for `yTicks`, x for `xTicks`). */
  pos: number;
  label: string;
}

export interface AxisProps {
  left: number;
  top: number;
  innerWidth: number;
  innerHeight: number;
  yTicks: AxisTick[];
  xTicks: AxisTick[];
  /** Emphasised zero-baseline y coordinate (drawn stronger than a gridline). */
  baselineY?: number;
}

/** Renders horizontal gridlines + left value labels + bottom category labels. */
export function Axis({ left, top, innerWidth, innerHeight, yTicks, xTicks, baselineY }: AxisProps) {
  const right = left + innerWidth;
  const bottom = top + innerHeight;
  return (
    <g aria-hidden>
      {yTicks.map((tick, i) => (
        <g key={`y-${i}`}>
          <line
            x1={left}
            x2={right}
            y1={tick.pos}
            y2={tick.pos}
            stroke="var(--viz-grid)"
            strokeWidth={1}
            shapeRendering="crispEdges"
          />
          <text
            x={left - 8}
            y={tick.pos}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={11}
            fill="var(--viz-label)"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {tick.label}
          </text>
        </g>
      ))}
      {typeof baselineY === 'number' ? (
        <line
          x1={left}
          x2={right}
          y1={baselineY}
          y2={baselineY}
          stroke="var(--viz-baseline)"
          strokeWidth={1.5}
          shapeRendering="crispEdges"
        />
      ) : null}
      {xTicks.map((tick, i) => (
        <text
          key={`x-${i}`}
          x={tick.pos}
          y={bottom + 16}
          textAnchor="middle"
          fontSize={11}
          fill="var(--viz-label)"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {tick.label}
        </text>
      ))}
    </g>
  );
}
